import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Loader2, Plus, Edit2, Trash2, X, Check, Search } from 'lucide-react';
import { Layout } from '../components/Layout';

interface DepartmentGroup {
  id: string;
  name: string;
  departments: string[];
  createdAt: string;
}

export function DepartmentMappings() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DepartmentGroup | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    departments: [] as string[]
  });

  const [searchQuery, setSearchQuery] = useState('');

  // Fetch groups
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['department-groups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/department-groups');
      return res.data.data as DepartmentGroup[];
    }
  });

  // Fetch distinct departments for selection
  const { data: distinctDepartmentsData } = useQuery({
    queryKey: ['departments-distinct'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/departments/distinct');
      return res.data.data as string[];
    }
  });

  const distinctDepartments = distinctDepartmentsData || [];
  const groups = groupsData || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return apiClient.post('/admin/department-groups', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-groups'] });
      closeModal();
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      return apiClient.put(`/admin/department-groups/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-groups'] });
      closeModal();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiClient.delete(`/admin/department-groups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-groups'] });
    }
  });

  const openModal = (group?: DepartmentGroup) => {
    if (group) {
      setEditingGroup(group);
      setFormData({ name: group.name, departments: group.departments });
    } else {
      setEditingGroup(null);
      setFormData({ name: '', departments: [] });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingGroup(null);
    setFormData({ name: '', departments: [] });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this committee?')) {
      deleteMutation.mutate(id);
    }
  };

  const toggleDepartment = (dept: string) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.includes(dept)
        ? prev.departments.filter(d => d !== dept)
        : [...prev.departments, dept]
    }));
  };

  const allMappedDepartments = new Set(
    groups
      .filter(g => g.id !== editingGroup?.id)
      .flatMap(g => g.departments)
  );

  const filteredDepartments = distinctDepartments.filter(d =>
    !allMappedDepartments.has(d) && d.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout title="Department Groupings">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight font-display">Department Master</h1>
            <p className="text-sm text-slate-500 mt-1">Group multiple departments into a single entity (e.g., Engineering Department)</p>
          </div>
          <button
            onClick={() => openModal()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-md shadow-indigo-600/20"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Committee</span>
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {isLoadingGroups ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-6 py-4 text-xs uppercase font-black tracking-wider text-slate-600 w-1/3">Committee Name</th>
                    <th className="px-6 py-4 text-xs uppercase font-black tracking-wider text-slate-600">Mapped Departments</th>
                    <th className="px-6 py-4 text-xs uppercase font-black tracking-wider text-slate-600 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {groups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-12 text-center text-slate-500 text-sm">
                        No department groups created yet.
                      </td>
                    </tr>
                  ) : (
                    groups.map((group) => (
                      <tr key={group.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-700 font-bold rounded-lg text-sm border border-indigo-100 shadow-sm">
                            {group.name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {group.departments.map(dept => (
                              <span key={dept} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-md border border-slate-200">
                                {dept}
                              </span>
                            ))}
                            {group.departments.length === 0 && (
                              <span className="text-xs text-slate-400 italic">No departments selected</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openModal(group)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit Group"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(group.id)}
                              className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              title="Delete Group"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h2 className="text-lg font-black text-slate-800 font-display">
                {editingGroup ? 'Edit Committee' : 'Create New Committee'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form id="group-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs uppercase font-bold text-slate-600 tracking-wider mb-2">Committee Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Engineering Department"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                  <p className="text-[10px] text-slate-500 mt-1.5 ml-1">Must be unique (e.g., Engineering Department, Finance Department, HR Department)</p>
                </div>

                <div>
                  <label className="block text-xs uppercase font-bold text-slate-600 tracking-wider mb-2">Map Departments</label>

                  <div className="relative mb-3">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search existing departments..."
                      className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                    />
                  </div>

                  <div className="border border-slate-200 rounded-xl bg-slate-50 max-h-[250px] overflow-y-auto p-2">
                    {distinctDepartments.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No departments found in the system.
                      </div>
                    ) : filteredDepartments.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No departments match your search.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {filteredDepartments.map(dept => {
                          const isSelected = formData.departments.includes(dept);
                          return (
                            <div
                              key={dept}
                              onClick={() => toggleDepartment(dept)}
                              className={`flex items-center px-3 py-2.5 rounded-lg cursor-pointer transition-all ${isSelected ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-100 text-slate-700'
                                }`}
                            >
                              <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 bg-white'
                                }`}>
                                {isSelected && <Check className="w-3 h-3" />}
                              </div>
                              <span className={`text-sm ${isSelected ? 'font-bold' : 'font-medium'}`}>
                                {dept}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-5 py-2.5 text-sm font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="group-form"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-md shadow-indigo-600/20 disabled:opacity-50"
              >
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{editingGroup ? 'Save Changes' : 'Create Committee'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
